import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { loadSheetData } from '@/data/load';
import { recountTime } from '@/data/nights';
import { onStorageError, type StorageError } from '@/data/write';
import { businessDateFor } from '@/domain/businessDate';
import type { SheetData } from '@/domain/sheet';
import type { BusinessDate } from '@/domain/types';
import { useSession } from './providers';

export function useStorageError(): [StorageError | null, () => void] {
  const [err, setErr] = useState<StorageError | null>(null);
  useEffect(() => onStorageError(setErr), []);
  return [err, () => setErr(null)];
}

/**
 * Tonight's business date (a count at 2 AM belongs to the night before), checked every
 * minute so a tablet left on overnight rolls over at the cutoff.
 */
export function useBusinessToday(): BusinessDate {
  const { settings } = useSession();
  return useBusinessDateAt(settings.cutoff);
}

/** The oldest night that can still be recounted (see `recountDay`), kept current the same way. */
export function useRecountDay(): BusinessDate {
  const { settings } = useSession();
  return useBusinessDateAt(recountTime(settings));
}

function useBusinessDateAt(cutoff: string): BusinessDate {
  const { settings } = useSession();
  const compute = () => businessDateFor(new Date(), settings.timezone, cutoff);
  const [day, setDay] = useState(compute);
  useEffect(() => {
    setDay(compute());
    const t = window.setInterval(() => setDay(compute()), 60_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.timezone, cutoff]);
  return day;
}

/** Live sheet data for nights in [from, to] (or everything). Re-reads when anything changes. */
export function useSheetData(from?: BusinessDate, to?: BusinessDate): SheetData | undefined {
  return useLiveQuery(() => loadSheetData(from, to), [from, to]);
}

/**
 * Keeps the screen on while mounted (Screen Wake Lock API), so a tablet doesn't dim halfway
 * through a count. Silently does nothing where unsupported or refused.
 */
export function useWakeLock(active = true): void {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;
    const request = async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) await l.release();
        else lock = l;
      } catch {
        /* not allowed right now; the screen just follows its normal timeout */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => {});
    };
  }, [active]);
}
